import moment from "moment-timezone";
import { getDateTokens, type OrgDateFormat } from "@/helpers/dateFormat";
import timezones from "@/helpers/timezones";

// LSP-19483: one rule for when a time reads as "an hour ago" and when it reads as a date.
//
// The notifications panel showed ONLY relative time, with no exact date-time anywhere — not even on
// hover — while the notification's own title embedded an absolute date ("… on 1/9/2026"). Sessions
// and Projects used absolute dates in the table view and "x days ago" on the card view of the very
// same rows. Both are decided here now, so a screen cannot pick its own rule.
//
// The rule: inside 24 hours, relative ("an hour ago") — that is what the reader wants for something
// that just happened. Older than 24 hours, the absolute date-time. Either way `exactLabel` carries
// the full date-time, so the caller can always expose it as a tooltip and the exact moment is never
// unavailable.
//
// LSP-19482 owns which absolute STYLE is used; this file only decides relative-vs-absolute and
// takes the style from the shared tokens.

const RELATIVE_WINDOW_HOURS = 24;

/** The non-ISO timestamp shape the API also returns; helpers/timezones.js parses with the same one. */
const DB_TIMESTAMP_FORMAT = "YYYY-MM-DD HH:mm:ss.SSS ZZ";

export interface DisplayTime {
	/** What to render: relative inside the window, absolute outside it. */
	label: string;
	/** Always the full date-time — use it as the title/tooltip so the exact moment is reachable. */
	exactLabel: string;
	/** True when `label` is relative, so a caller can style or announce it differently. */
	isRelative: boolean;
}

function tokensFor(dateFormat?: OrgDateFormat | string | null) {
	return getDateTokens((dateFormat ?? timezones.getDateFormat()) as OrgDateFormat);
}

/**
 * Parses a timestamp the way helpers/timezones.js does — the explicit DB shape first, then ISO —
 * rather than letting a bare moment(value) fall through to `new Date()`.
 *
 * The API returns both shapes ("2026-09-03T03:14:00.000Z" and "2026-09-03 03:14:00.000 +00:00").
 * moment only recognises the first, and warns that its Date() fallback for the second "is not
 * reliable across all browsers and versions" — so the Sessions Date cell would have depended on
 * browser-specific parsing for exactly the values the rest of this app parses explicitly. Both
 * render in the reader's zone, matching every other date on the screen.
 */
function parseInReaderZone(value: string | number | Date) {
	const zone = moment.tz.guess();
	// Only STRINGS need the explicit formats. A Date or epoch is already unambiguous, and
	// String()-ing a Date produces "Mon Jan 01 2024 …", which matches neither format and parses as
	// invalid — that is how this helper first returned empty labels for every absolute row.
	if (typeof value !== "string") return moment.tz(value, zone);
	return moment.tz(value, [DB_TIMESTAMP_FORMAT, moment.ISO_8601 as never], zone);
}

/** The single absolute date-time style (LSP-19482). Empty for a missing or unparseable value. */
export function absoluteDateTime(value: string | number | Date | null | undefined, dateFormat?: OrgDateFormat | string | null): string {
	if (value === null || value === undefined || value === "") return "";
	const instant = parseInReaderZone(value);
	if (!instant.isValid()) return "";
	return instant.format(`${tokensFor(dateFormat).dateShortMonthCommaYear} h:mm A`);
}

/**
 * The single absolute short-date style (LSP-19482). Empty for a missing or unparseable value:
 * unguarded, moment(null) formats as the literal "Invalid date" and moment(undefined) is "now",
 * so a row with no createdAt would print fabricated creation metadata (ls-heroku-ci).
 */
export function absoluteDate(value: string | number | Date | null | undefined, dateFormat?: OrgDateFormat | string | null): string {
	if (value === null || value === undefined || value === "") return "";
	const instant = parseInReaderZone(value);
	if (!instant.isValid()) return "";
	return instant.format(tokensFor(dateFormat).dateShortMonthCommaYear);
}

/**
 * Applies the one time rule. `now` is injectable so tests do not depend on the wall clock.
 */
export function displayTime(
	value: string | number | Date | null | undefined,
	dateFormat?: OrgDateFormat | string | null,
	now: moment.MomentInput = undefined
): DisplayTime {
	if (value === null || value === undefined || value === "") {
		return { label: "", exactLabel: "", isRelative: false };
	}

	const instant = parseInReaderZone(value);
	if (!instant.isValid()) {
		return { label: "", exactLabel: "", isRelative: false };
	}

	const reference = now === undefined ? moment() : moment(now);
	const exactLabel = absoluteDateTime(instant.toDate(), dateFormat);
	// Future instants (a scheduled call) read naturally as relative too — "in 2 hours".
	const withinWindow = Math.abs(reference.diff(instant, "hours", true)) < RELATIVE_WINDOW_HOURS;

	return withinWindow
		? { label: instant.from(reference), exactLabel, isRelative: true }
		: { label: exactLabel, exactLabel, isRelative: false };
}

/** Convenience for a template that only needs the visible string. */
export function displayTimeLabel(
	value: string | number | Date | null | undefined,
	dateFormat?: OrgDateFormat | string | null,
	now: moment.MomentInput = undefined
): string {
	return displayTime(value, dateFormat, now).label;
}

export { RELATIVE_WINDOW_HOURS };
